(() => {
  const STORAGE_KEY = 'leobela:wishlist:v1';
  const CHANGE_EVENT = 'leobela:wishlist:change';
  const READY_EVENT = 'leobela:wishlist:ready';
  const VERSION = 1;
  const ITEM_LIMIT = 100;

  const normalizeStore = (value) => {
    if (!value || value.version !== VERSION || !Array.isArray(value.items)) {
      return { version: VERSION, updatedAt: 0, items: [] };
    }

    const productIds = new Set();
    const items = [];

    value.items.forEach((item) => {
      if (items.length >= ITEM_LIMIT || !item) return;

      const productId = String(item.productId || '').trim();
      const handle = typeof item.handle === 'string' ? item.handle.trim() : '';
      const addedAt = Number(item.addedAt);

      if (!productId || !handle || productIds.has(productId)) return;

      productIds.add(productId);
      items.push({
        productId,
        handle,
        addedAt: Number.isFinite(addedAt) ? addedAt : 0,
      });
    });

    return {
      version: VERSION,
      updatedAt: Number.isFinite(Number(value.updatedAt)) ? Number(value.updatedAt) : 0,
      items,
    };
  };

  const readStore = () => {
    try {
      return normalizeStore(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null'));
    } catch (error) {
      return normalizeStore(null);
    }
  };

  let store = readStore();
  let savedProductIds = new Set(store.items.map((item) => item.productId));

  const writeStore = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (error) {
      // The in-memory wishlist remains usable when persistent storage is unavailable.
    }
  };

  const syncControl = (element) => {
    const button = element.querySelector('.wishlist-button__control');
    if (!button) return;

    const isSaved = savedProductIds.has(element.dataset.wishlistProductId || '');
    button.setAttribute('aria-pressed', String(isSaved));
    button.setAttribute(
      'aria-label',
      isSaved ? button.dataset.wishlistRemoveLabel : button.dataset.wishlistAddLabel
    );
  };

  const syncControls = (root = document) => {
    if (root.matches?.('wishlist-button')) syncControl(root);
    root.querySelectorAll?.('wishlist-button').forEach(syncControl);
  };

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      syncControl(this);
    }
  }

  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', WishlistButton);
  }

  const publishChange = (productId, isSaved) => {
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: { productId, isSaved },
      })
    );
  };

  const removeProduct = (productId) => {
    const normalizedProductId = String(productId || '').trim();
    if (!normalizedProductId || !savedProductIds.has(normalizedProductId)) return false;

    store.items = store.items.filter((item) => item.productId !== normalizedProductId);
    store.updatedAt = Date.now();
    savedProductIds = new Set(store.items.map((item) => item.productId));
    writeStore();
    publishChange(normalizedProductId, false);

    return true;
  };

  const clearWishlist = () => {
    if (store.items.length === 0) return false;

    store.items = [];
    store.updatedAt = Date.now();
    savedProductIds = new Set();
    writeStore();
    publishChange(null, null);

    return true;
  };

  window.LeobelaWishlist = Object.freeze({
    getItems: () => store.items.map((item) => ({ ...item })),
    remove: removeProduct,
    clear: clearWishlist,
  });

  document.dispatchEvent(new CustomEvent(READY_EVENT));

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.wishlist-button__control');
    if (!button) return;

    const control = button.closest('wishlist-button');
    const productId = control?.dataset.wishlistProductId?.trim();
    const handle = control?.dataset.wishlistProductHandle?.trim();
    if (!productId || !handle) return;

    const isSaved = savedProductIds.has(productId);
    const timestamp = Date.now();

    if (isSaved) {
      removeProduct(productId);
      return;
    } else {
      store.items = [
        { productId, handle, addedAt: timestamp },
        ...store.items.filter((item) => item.productId !== productId),
      ].slice(0, ITEM_LIMIT);
    }

    store.updatedAt = timestamp;
    savedProductIds = new Set(store.items.map((item) => item.productId));
    writeStore();
    publishChange(productId, !isSaved);
  });

  document.addEventListener(CHANGE_EVENT, (event) => {
    const productId = event.detail?.productId;

    if (!productId) {
      syncControls();
      return;
    }

    document
      .querySelectorAll(`wishlist-button[data-wishlist-product-id="${CSS.escape(productId)}"]`)
      .forEach(syncControl);
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;

    store = readStore();
    savedProductIds = new Set(store.items.map((item) => item.productId));
    publishChange(null, null);
  });

  const syncHeaderWishlistCount = () => {
    const count = store.items.length;
    const visibleCount = count > 99 ? '99+' : String(count);

    document.querySelectorAll('[data-wishlist-count-bubble]').forEach((bubble) => {
      const value = bubble.querySelector('[data-wishlist-count]');

      if (value) value.textContent = visibleCount;
      bubble.hidden = count === 0;
    });

    document.querySelectorAll('[data-wishlist-header-link]').forEach((link) => {
      link.setAttribute(
        'aria-label',
        count > 0 ? `Patikusios prekės (${count})` : 'Patikusios prekės'
      );
    });
  };

  syncHeaderWishlistCount();
  document.addEventListener(CHANGE_EVENT, syncHeaderWishlistCount);

  document.addEventListener('shopify:section:load', (event) => {
    syncControls(event.target);
    syncHeaderWishlistCount();
  });
})();
